/**
 * PageHeader (TMMPDM-0034, PAGE-001 / 002 / 003 / 006 / 008) — the top of every collection,
 * dashboard and settings page: the `h1` title, a `body1` subtitle, a scope line when the page is
 * narrowed, and the page's actions on the right (one contained primary; outlined secondaries; rare
 * ones in an overflow menu). Sticky under the app bar. Below `md` the actions wrap under the title.
 *
 * `help` sits beside the title (a field-help "?"); `meta` under the subtitle (an "As of" line);
 * `controls` are the page's own selectors (period, reseller) shown before the actions.
 *
 * One `h1` element for the life of a page (TMM-B7, TMMPDM-0077): a page that loads a record draws
 * this same header — at the same tree position — in its loading, failed and loaded states, so the
 * title that resolves is the same element with new text, announced once, never a replaced one.
 */
import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  HeaderActions,
  HeaderCrumbs,
  StickyHeader,
  type Crumb,
  type HeaderAction,
} from './headerChrome';
import { titleParts, usePageTitle } from './usePageTitle';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Visible scope when the page is narrowed ("Reseller: Acme Resale"). */
  scope?: string;
  primaryAction?: HeaderAction;
  secondaryActions?: HeaderAction[];
  overflowActions?: HeaderAction[];
  breadcrumbs?: Crumb[];
  help?: ReactNode;
  meta?: ReactNode;
  controls?: ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  scope,
  primaryAction,
  secondaryActions,
  overflowActions,
  breadcrumbs,
  help,
  meta,
  controls,
}: PageHeaderProps) {
  usePageTitle(...titleParts(title, breadcrumbs));
  return (
    <StickyHeader>
      {breadcrumbs && breadcrumbs.length > 0 ? <HeaderCrumbs crumbs={breadcrumbs} /> : null}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'flex-end' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h1" component="h1" tabIndex={-1}>
              {title}
            </Typography>
            {help}
          </Stack>
          {subtitle ? (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
          {scope ? (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ alignItems: 'center', mt: 0.5, color: 'text.secondary' }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              <Typography variant="caption">{scope}</Typography>
            </Stack>
          ) : null}
          {meta}
        </Box>
        {controls || primaryAction || secondaryActions || overflowActions ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'center' }, flexShrink: 0 }}
          >
            {controls}
            <HeaderActions
              primaryAction={primaryAction}
              secondaryActions={secondaryActions}
              overflowActions={overflowActions}
            />
          </Stack>
        ) : null}
      </Stack>
    </StickyHeader>
  );
}
